import React from "react"

import { Button, ButtonVariant } from "../button/Button"
import { DeviceInfo } from "../device/DeviceInfo"
import { withDeviceInfo } from "../device/withDeviceInfo"
import { HotKeySet } from "../keyboard/HotKeyResponder"
import { Text } from "../text/Text"
import { CameraPopup } from "./CameraPopup"

interface InjectedProps {
  /**
   * Injected by withDeviceInfo() HoC wrapper.
   */
  deviceInfo: DeviceInfo | undefined
}

export interface ImageCaptureProps extends InjectedProps {
  /**
   * Form field name.
   */
  name: string

  /**
   * Disable automatic form submission after image capture.
   *
   * The default behaviour is for the form to automatically submit after the image is captured.
   */
  disableAutoSubmit?: boolean

  /**
   * JPEG image compression quality factor.
   * Range: 0.0 (lowest quality, smallest size) to 1.0 (highest quality, largest size).
   * Applies only to JPEG output; ignored when `imageType` is `"image/png"` because
   * PNG is a lossless format that does not accept a quality parameter.
   * @default 0.7
   */
  imageQuality?: number

  /**
   * Output image format for the captured image.
   *
   * - `"image/jpeg"` (default): Lossy compression controlled by `imageQuality`.
   *   Good balance of quality and file size for most use cases.
   * - `"image/png"`: Lossless encoding that preserves sharp text edges.
   *   Recommended for OCR use cases where compression artifacts degrade
   *   text recognition accuracy. When using PNG, `imageQuality` has no effect
   *   (PNG does not accept a quality parameter). Use `maxWidth` and `maxHeight`
   *   to control output size and stay within the ~500 KB TTS request size limit.
   *
   * @default "image/jpeg"
   */
  imageType?: "image/jpeg" | "image/png"

  /**
   * Maximum width in pixels for the output image.
   * Image will be resized to fit within this width while preserving aspect ratio.
   * If only maxWidth is specified, height scales proportionally.
   */
  maxWidth?: number

  /**
   * Maximum height in pixels for the output image.
   * Image will be resized to fit within this height while preserving aspect ratio.
   * If only maxHeight is specified, width scales proportionally.
   */
  maxHeight?: number

  /**
   * Hot key or set of hot keys that triggers the capture button.
   */
  hotKey?: HotKeySet

  /**
   * Button variant for the capture trigger button.
   * @default "primary"
   */
  buttonVariant?: ButtonVariant

  /**
   * Automatically open the camera when the component mounts.
   *
   * When `true`, the camera will open immediately after the component mounts
   * and camera availability is detected — without requiring the user to press
   * the capture button first.
   *
   * On desktop, uses WebRTC mode when a camera is detected.
   * On mobile (managed devices), programmatically opens the native file
   * input camera. This relies on the device not enforcing the browser's
   * trusted user gesture requirement (e.g. Zebra/TC devices with custom
   * WebView). On standard mobile browsers this may be silently ignored.
   *
   * Has no effect when no camera is detected on desktop; in that case the
   * user must tap the capture button manually to open the file picker.
   *
   * @default false
   */
  autoOpenCamera?: boolean

  /**
   * Callback invoked with the processed base64 image data after capture.
   * Called with disableAutoSubmit=true to allow the parent to handle
   * the image data before any form submission occurs.
   *
   * Main purpose is for frontend OCR integration.
   */
  onCapture?: (base64Image: string) => void

}

interface ImageCaptureState {
  /**
   * Stringified JSON of captured image data.
   * Stored as string for efficiency (avoids re-stringifying on every render).
   * Will be submitted to form as { type, data } object.
   */
  imageDataJson: string

  /**
   * Capture mode determined on mount based on availability video input devices
   * "webrtc" when a video input device is detected, "file" otherwise.
   */
  captureMode: "file" | "webrtc"

  /**
   * Whether the CameraPopup is currently open.
   */
  popupOpen: boolean
}

/**
 * Image capture component for capturing images from device camera.
 *
 * Features:
 * - Primary button trigger instead of default file input UI
 * - Optional aspect-ratio-preserving resize with maxWidth/maxHeight
 * - Configurable output format via `imageType` (JPEG or PNG)
 * - Compresses images to JPEG with configurable quality (PNG is lossless)
 * - Auto-submits form by default (can be disabled)
 *
 * Resize behavior:
 * - Preserves aspect ratio - image never distorts
 * - Fits within both maxWidth and maxHeight constraints
 * - Never upscales - only downscales if image exceeds constraints
 *
 * For 500 KB TTS limit, recommended settings:
 * - maxWidth: 1280, maxHeight: 720, imageQuality: 0.7
 * - Expected output: 250-400 KB base64
 */
export class ImageCaptureBase extends React.PureComponent<ImageCaptureProps, ImageCaptureState> {

  public static defaultProps = {
    imageType: "image/jpeg" as const,
    imageQuality: 0.7,
    buttonVariant: "primary" as const,
    autoOpenCamera: false,
  }

  private readonly fileInputRef = React.createRef<HTMLInputElement>()
  private readonly imageDataInputRef = React.createRef<HTMLInputElement>()

  // Guards async work in detectCameraAvailability from touching state after
  // unmount (e.g. when DateCapture remounts this via key={retryCount}).
  private mounted = false

  public constructor(props: ImageCaptureProps) {
    super(props)
    this.state = {
      imageDataJson: "",
      captureMode: "file",
      popupOpen: false,
    }
  }

  public componentDidMount(): void {
    this.mounted = true
    this.detectCameraAvailability()
  }

  public componentWillUnmount(): void {
    this.mounted = false
  }

  /**
   * Detect camera availability by checking for video input devices.
   * Sets captureMode to "webrtc" if a video input is found, stays "file" otherwise.
   * Silently catches errors and stays in file mode.
   * When autoOpen is true, opens the camera (or file picker) after detection.
   */
  private async detectCameraAvailability(): Promise<void> {
    if (this.props.deviceInfo?.deviceType === "mobile") {
      if (this.props.autoOpenCamera) {
        this.fileInputRef.current?.click()
      }
      return // stays in "file" mode, uses native camera input
    }
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return // stays in "file" mode (default)
      }
      const devices = await navigator.mediaDevices.enumerateDevices()
      if (!this.mounted) {
        return
      }
      const hasVideoInput = devices.some((d) => d.kind === "videoinput")
      if (hasVideoInput) {
        this.setState({ captureMode: "webrtc" }, () => {
          if (!this.mounted) {
            return
          }
          if (this.props.autoOpenCamera) {
            this.setState({ popupOpen: true })
          }
        })
      }
    } catch {
      // Detection failed, stay in file mode
    }
  }

  /**
   * Handle confirmed image from CameraPopup.
   * Processes the captured data URL through the existing image pipeline,
   * stores the result as JSON in the hidden input, closes the popup,
   * and auto-submits if enabled.
   */
  private handlePopupConfirm = async (dataUrl: string) => {
    const { imageType, imageQuality, onCapture } = this.props
    this.setState({ popupOpen: false })

    const processedBase64 = await this.processImage(dataUrl, imageType!, imageQuality!)
    const imageDataJson = JSON.stringify({
      type: imageType,
      data: processedBase64
    })

    this.setState({ imageDataJson }, () => {
      if (onCapture) {
        onCapture(processedBase64)
      }
      if (!this.props.disableAutoSubmit) {
        this.submitForm()
      }
    })
  }

  /**
   * Handle user-initiated cancel from CameraPopup.
   * Closes the popup without any fallback.
   */
  private handlePopupCancel = () => {
    this.setState({ popupOpen: false })
  }

  /**
   * Handle camera acquisition error from CameraPopup.
   * Closes the popup and falls back to file input.
   */
  private handlePopupError = () => {
    this.setState({ popupOpen: false })
    this.fileInputRef.current?.click()
  }

  /**
   * Resolve the capture width requested from the camera. Anchors the request to
   * the desired output width (`maxWidth`) so the stream can come up to that
   * resolution instead of the browser's 640x480 default. Returns undefined when
   * `maxWidth` is not set, leaving the stream resolution unconstrained as before.
   * Requested via an `ideal` constraint, so the camera degrades gracefully when
   * it cannot match.
   */
  private getRequestedCaptureWidth = (): number | undefined => {
    return this.props.maxWidth
  }

  /**
   * Resolve the capture height requested from the camera. See
   * `getRequestedCaptureWidth` for behavior.
   */
  private getRequestedCaptureHeight = (): number | undefined => {
    return this.props.maxHeight
  }

  private handleButtonClick = () => {
    if (this.state.captureMode === "webrtc") {
      this.setState({ popupOpen: true })
    } else {
      this.fileInputRef.current?.click()
    }
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target?.result as string)
      reader.onerror = () => resolve("")
      reader.readAsDataURL(file)
    })
  }

  /**
   * Calculate output dimensions for a given image size, respecting maxWidth/maxHeight
   * constraints while preserving aspect ratio and never upscaling.
   */
  private calculateOutputDimensions(
    originalWidth: number,
    originalHeight: number
  ): { width: number; height: number } {
    const { maxWidth, maxHeight } = this.props

    if (!maxWidth && !maxHeight) {
      return { width: originalWidth, height: originalHeight }
    }

    // Calculate scale factors for each constraint
    const widthScale = maxWidth ? maxWidth / originalWidth : Infinity
    const heightScale = maxHeight ? maxHeight / originalHeight : Infinity

    // Use the smaller scale factor to ensure image fits within both constraints
    const scale = Math.min(widthScale, heightScale, 1) // Don't upscale

    return {
      width: Math.round(originalWidth * scale),
      height: Math.round(originalHeight * scale)
    }
  }

  /**
   * Process image by resizing (if specified) and compressing to the specified format.
   * @param dataUrl - Image data URL from FileReader
   * @param imageType - Output MIME type ("image/jpeg" or "image/png")
   * @param imageQuality - JPEG compression quality (ignored for PNG)
   * @returns Promise resolving to the processed base64 data string
   */
  private processImage(
    dataUrl: string,
    imageType: "image/jpeg" | "image/png",
    imageQuality: number,
  ): Promise<string> {

    return new Promise((resolve) => {
      const img = new Image()

      img.onload = () => {
        const { width: outputWidth, height: outputHeight } =
          this.calculateOutputDimensions(img.width, img.height)

        // Create canvas with output dimensions
        const canvas = document.createElement("canvas")
        canvas.width = outputWidth
        canvas.height = outputHeight

        const ctx = canvas.getContext("2d")
        if (!ctx) {
          // Fallback: return original base64 if canvas context fails
          resolve(dataUrl.split(",")[1])
          return
        }

        // Draw resized image
        ctx.drawImage(img, 0, 0, outputWidth, outputHeight)

        // PNG is lossless — quality parameter is only meaningful for JPEG
        let processedDataUrl: string
        switch (imageType) {
          case "image/png":
            processedDataUrl = canvas.toDataURL("image/png")
            break
          case "image/jpeg":
            processedDataUrl = canvas.toDataURL("image/jpeg", imageQuality)
            break
          default: {
            // Errors at compile if new value added to imageType but not handled in this switch
            const exhaustiveCheck: never = imageType
            throw new Error(
              `Unsupported imageType: ${exhaustiveCheck}`,
            )
          }
        }

        resolve(processedDataUrl.split(",")[1])
      }

      img.onerror = () => {
        // Fallback: return original base64 if image loading fails
        resolve(dataUrl.split(",")[1])
      }

      img.src = dataUrl
    })
  }

  private handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { imageType, imageQuality, onCapture } = this.props
    const files = event.target.files
    const fileInput = event.target

    if (files && files[0]) {
      const file = files[0]

      const dataUrl = await this.readFileAsDataUrl(file)
      const processedBase64 = await this.processImage(dataUrl, imageType!, imageQuality!)

      // Stringify immediately to avoid stringifying on every render
      const imageDataJson = JSON.stringify({
        type: imageType,
        data: processedBase64
      })

      // Clear file input to prevent CORS/403 errors on form submission
      // File input's value must be cleared because browsers apply strict security
      // policies when forms with file inputs are submitted programmatically
      fileInput.value = ""

      this.setState({ imageDataJson }, () => {
        if (onCapture) {
          onCapture(processedBase64)
        }
        // If necessary, auto-submit after image is selected
        if (!this.props.disableAutoSubmit) {
          this.submitForm()
        }
      })
    }
  }

  private submitForm = () => {
    const formElement = this.imageDataInputRef.current?.form
    if (formElement) {
      formElement.requestSubmit()
    }
  }

  public render() {
      const {
        name,
        children = <Text>Capture Image</Text>,
        hotKey,
        buttonVariant,
      } = this.props
      const { imageDataJson, popupOpen } = this.state

      return (
        <div className="image-capture">
          <input
            ref={this.fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={this.handleChange}
          />

          {popupOpen && (
            <CameraPopup
              requestedWidth={this.getRequestedCaptureWidth()}
              requestedHeight={this.getRequestedCaptureHeight()}
              onConfirm={this.handlePopupConfirm}
              onCancel={this.handlePopupCancel}
              onError={this.handlePopupError}
            />
          )}

          <Button
            variant={buttonVariant}
            fluid={true}
            hotKey={hotKey}
            onClick={this.handleButtonClick}
          >
            {children}
          </Button>

          <input
            ref={this.imageDataInputRef}
            type="hidden"
            name={`${name}$json`}
            value={imageDataJson}
          />
        </div>
      )
    }

}

export const ImageCapture = Object.assign(
  withDeviceInfo(ImageCaptureBase),
  {
    displayName: "ImageCapture",
  },
)
